export const ROLE_TYPES = {
  OWNER: '0',
  ADMIN: '1',
  INVITED: '2',
  USER: '3',
  CONTENT_PROPOSAL: '4',
  CONTENT_APPROVAL: '5',
  READ_ONLY: '6',
  TEAM_OWNER: '7',
  SUPER_ADMIN: '8',
  // Group Security v3: Billing role — reaches Billing & Orders; content is read-only (D4).
  // CONTENT_PROPOSAL ('4') / CONTENT_APPROVAL ('5') are soft-deprecated (D7): kept here so
  // already-assigned users still resolve, but hidden from every role picker.
  BILLING: '9',
  /**
   * Pre-built content roles (Ted, 2026-09-02). Two-CHARACTER codes — the first built-in roles that
   * are not a single digit, because '0'–'9' were exhausted. Anything that told a system role from a
   * custom role by `role.length === 1` must use `isSystemRoleCode()` below instead. Mirrors the
   * backend `ROLE_TYPES` in `optisigns-open-api/src/app/shared/common-defs.ts`.
   */
  CONTENT_CREATOR: '10',
  CONTENT_MANAGER: '11',
} as const;

/**
 * Every built-in role code — THE discriminator between a system role (permissions come from the
 * backend's ROLE_VALUE_TYPES) and a custom role (a `roleSecurity` document id).
 */
const SYSTEM_ROLE_CODES: ReadonlySet<string> = new Set<string>(Object.values(ROLE_TYPES));

/** True when `role` is a built-in role code, false for a custom-role id (or empty). */
export const isSystemRoleCode = (role?: string | null): boolean =>
  !!role && SYSTEM_ROLE_CODES.has(String(role));

/** True when `role` names a CUSTOM role — a `roleSecurity` document id. */
export const isCustomRoleId = (role?: string | null): boolean =>
  !!role && !SYSTEM_ROLE_CODES.has(String(role));

export type RoleType = (typeof ROLE_TYPES)[keyof typeof ROLE_TYPES];

export const DEVICE_STATUS = {
  OFFLINE: 0,
  ONLINE: 1,
  ONLINE_HDMI_DISCONNECT: 2,
  ONLINE_WRONG_TIME: 3,
  OFFLINE_WRONG_TIME: 4,
} as const;

export type DeviceStatus = (typeof DEVICE_STATUS)[keyof typeof DEVICE_STATUS];

export const MEDIA_TYPES = {
  NONE: 'none',
  ASSET: 'asset',
  PLAYLIST: 'playlist',
  SCHEDULE: 'schedule',
} as const;

export type MediaType = (typeof MEDIA_TYPES)[keyof typeof MEDIA_TYPES];

export const ORIENTATION_TYPES = {
  LANDSCAPE: 'landscape',
  ROTATE_90: 'rotate_90',
  ROTATE_180: 'rotate_180',
  ROTATE_270: 'rotate_270',
} as const;

export type OrientationType = (typeof ORIENTATION_TYPES)[keyof typeof ORIENTATION_TYPES];

export const SCALE_TYPES = {
  NONE: 'none',
  FIT: 'fit',
  STRETCH: 'stretch',
  ZOOM: 'zoom',
} as const;

export type ScaleType = (typeof SCALE_TYPES)[keyof typeof SCALE_TYPES];

export const PUSH_TO_SCREEN_TYPES = {
  NOW: 'now',
  SCHEDULE: 'schedule',
  TEMPORARILY: 'temporarily',
  GROUPS: 'groups',
} as const;

export type PushToScreenType = (typeof PUSH_TO_SCREEN_TYPES)[keyof typeof PUSH_TO_SCREEN_TYPES];

export const PUSH_TO_SCREENS_AFTER_EXPIRED = {
  RECENTLY_PLAYED: 'recentlyPlayed',
  STOP_PLAYING: 'stopPlaying',
} as const;

export type PushToScreensAfterExpired =
  (typeof PUSH_TO_SCREENS_AFTER_EXPIRED)[keyof typeof PUSH_TO_SCREENS_AFTER_EXPIRED];

/** Map stored camelCase value → GraphQL enum key (UPPER_SNAKE_CASE).
 *  `.toUpperCase()` doesn't work here because `'recentlyPlayed'` → `'RECENTLYPLAYED'` ≠ `'RECENTLY_PLAYED'`. */
const _AFTER_EXPIRE_TO_GQL: Record<string, string> = Object.fromEntries(
  Object.entries(PUSH_TO_SCREENS_AFTER_EXPIRED).map(([key, value]) => [value, key]),
);
export const toGqlAfterExpire = (value: string): string => _AFTER_EXPIRE_TO_GQL[value] ?? value;

export const SORT_DIRECTION = {
  ASC: 'ASC',
  DESC: 'DESC',
} as const;

export type SortDirection = (typeof SORT_DIRECTION)[keyof typeof SORT_DIRECTION];

export const GROUP_TYPES = {
  DEVICE: 'device',
  PLAYLIST: 'playlist',
  SCHEDULE: 'schedule',
  ASSET: 'asset',
  ENGAGE: 'engage',
} as const;

export type GroupType = (typeof GROUP_TYPES)[keyof typeof GROUP_TYPES];

export const BACKGROUND_TYPES = {
  DEFAULT: 'default',
  COLOR: 'color',
  IMAGE: 'image',
  TRANSPARENT: 'transparent',
} as const;

export type BackgroundType = (typeof BACKGROUND_TYPES)[keyof typeof BACKGROUND_TYPES];

export const VIDEO_PLAYER_TYPES = {
  DEFAULT: 'default',
  HTML5: 'html5',
  OMX: 'omx',
  MPV: 'mpv',
} as const;

export type VideoPlayerType = (typeof VIDEO_PLAYER_TYPES)[keyof typeof VIDEO_PLAYER_TYPES];

export const REPEAT_SCHEDULE = {
  NONE: 'none',
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
  MONTHLY_DAY: 'monthlyDay',
  MONTHLY_END: 'monthlyOfEnd',
  MONTHLY_WEEK: 'monthlyOfWeek',
  WEEKLY_WEEKDAY: 'weeklyWeekday',
  WEEKLY_END: 'weeklyOfEnd',
  CUSTOM: 'custom',
  CUSTOM_SAVE: 'custom_save',
  HOURLY: 'hourly',
} as const;

export type RepeatSchedule = (typeof REPEAT_SCHEDULE)[keyof typeof REPEAT_SCHEDULE];

export const REPEAT_SCHEDULE_END = {
  NEVER: 'never',
  ON_DATE: 'on',
  AFTER: 'after',
} as const;

export type RepeatScheduleEnd = (typeof REPEAT_SCHEDULE_END)[keyof typeof REPEAT_SCHEDULE_END];

export const SCHEDULE_DEFAULT_TYPES = {
  NONE: 0,
  ACCOUNT_DEFAULT: 1,
  ASSET: 2,
  PLAYLIST: 3,
} as const;

export type ScheduleDefaultType =
  (typeof SCHEDULE_DEFAULT_TYPES)[keyof typeof SCHEDULE_DEFAULT_TYPES];

export const APPLY_SCHEDULE_ITEM_SCOPES = {
  SINGLE: 'SINGLE',
  ALL: 'ALL',
} as const;

export type ApplyScheduleItemScope =
  (typeof APPLY_SCHEDULE_ITEM_SCOPES)[keyof typeof APPLY_SCHEDULE_ITEM_SCOPES];

export const SCHEDULE_TYPES = {
  DEFAULT: 'default',
  CEC: 'CEC',
  LOCAL_PLAYLIST: 'LOCAL_PLAYLIST',
  OPERATIONAL_SCHEDULE: 'Ops',
  ASSET_EXPIRED: 'ASSET_EXPIRED',
} as const;

export type ScheduleType = (typeof SCHEDULE_TYPES)[keyof typeof SCHEDULE_TYPES];
